package se.mittpass.web.pages

import androidx.compose.runtime.*
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.PayslipDto
import se.mittpass.web.components.FaIcon

private val payslipScope = MainScope()

@Composable
fun MyPayslipsPage() {
    var payslips by remember { mutableStateOf<List<PayslipDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        payrollRepo.getMyPayslips().onSuccess {
            payslips = it
            loading = false
        }.onFailure {
            error = "Kunde inte hämta lönebesked"
            loading = false
        }
    }

    Div({
        style {
            property("max-width", "900px")
            property("margin", "0 auto")
            property("padding", "1rem")
        }
    }) {
        // ── Header ──
        Div({
            style {
                property("margin-bottom", "1.5rem")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("flex-wrap", "wrap")
                property("gap", "1rem")
            }
        }) {
            Div {
                H2({
                    style {
                        property("margin", "0 0 0.25rem 0")
                        property("font-size", "1.5rem")
                        property("font-weight", "800")
                        property("color", "var(--rp-text)")
                    }
                }) { Text("Mina lönebesked") }
                P({
                    style {
                        property("margin", "0")
                        property("color", "var(--rp-subtle)")
                        property("font-size", "0.9rem")
                    }
                }) { Text("Här kan du se och skriva ut eller spara dina lönebesked som PDF.") }
            }
        }

        if (loading) {
            Div({ style { property("text-align", "center"); property("padding", "3rem"); property("color", "var(--rp-subtle)") } }) {
                Text("Laddar dina lönebesked…")
            }
            return@Div
        }

        error?.let { err ->
            Div({
                style {
                    property("background", "var(--rp-err-bg)")
                    property("color", "var(--rp-err-text)")
                    property("padding", "1rem")
                    property("border-radius", "12px")
                    property("margin-bottom", "1rem")
                    property("font-weight", "600")
                }
            }) { Text(err) }
        }

        if (payslips.isEmpty()) {
            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border-radius", "16px")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("padding", "3rem 1.5rem")
                    property("text-align", "center")
                }
            }) {
                Div({
                    style {
                        property("width", "54px")
                        property("height", "54px")
                        property("border-radius", "50%")
                        property("background", "var(--rp-overlay)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("margin", "0 auto 1rem auto")
                    }
                }) {
                    FaIcon("fa-solid fa-file-invoice-dollar", size = "1.5rem", color = "var(--rp-muted)")
                }
                H3({ style { property("margin", "0 0 0.5rem 0"); property("font-size", "1.1rem"); property("color", "var(--rp-text)") } }) {
                    Text("Inga publicerade lönebesked än")
                }
                P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) {
                    Text("När din arbetsgivare skapar och godkänner lönebeskedet kommer det att synas här direkt.")
                }
            }
        } else {
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.85rem") } }) {
                payslips.forEach { slip ->
                    Div({
                        style {
                            property("background", "var(--rp-surface)")
                            property("border-radius", "16px")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("padding", "1.25rem 1.5rem")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "space-between")
                            property("flex-wrap", "wrap")
                            property("gap", "1rem")
                            property("box-shadow", "0 2px 8px rgba(0,0,0,0.02)")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "1rem") } }) {
                            Div({
                                style {
                                    property("width", "46px")
                                    property("height", "46px")
                                    property("border-radius", "12px")
                                    property("background", "var(--rp-ok-bg)")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("flex-shrink", "0")
                                }
                            }) {
                                FaIcon("fa-solid fa-file-pdf", size = "1.25rem", color = "var(--rp-ok-text)")
                            }

                            Div {
                                Div({ style { property("font-weight", "800"); property("font-size", "1.05rem"); property("color", "var(--rp-text)") } }) {
                                    Text("Löneperiod: ${slip.periodStart} – ${slip.periodEnd}")
                                }
                                Div({ style { property("font-size", "0.8rem"); property("color", "var(--rp-subtle)"); property("margin-top", "0.2rem") } }) {
                                    Text("Utbetalningsdag: ${slip.paymentDate} • Arbetad tid: ${slip.workedMinutes / 60}h ${slip.workedMinutes % 60}m")
                                }
                            }
                        }

                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "1.5rem") } }) {
                            Div({ style { property("text-align", "right") } }) {
                                Div({ style { property("font-size", "0.72rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("text-transform", "uppercase") } }) {
                                    Text("Utbetalas (netto)")
                                }
                                Div({ style { property("font-size", "1.3rem"); property("font-weight", "900"); property("color", "var(--rp-ok-text)") } }) {
                                    Text("${slip.netPaySek.toLong()} kr")
                                }
                                Div({ style { property("font-size", "0.72rem"); property("color", "var(--rp-muted)") } }) {
                                    Text("Brutto: ${slip.grossPaySek.toLong()} kr")
                                }
                            }

                            Button({
                                onClick {
                                    val token = se.mittpass.web.authStorage.getToken() ?: ""
                                    val url = "/payslips/${slip.id}/view" + if (token.isNotEmpty()) "?token=$token" else ""
                                    kotlinx.browser.window.open(url, "_blank")
                                }
                                style {
                                    property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))")
                                    property("color", "white")
                                    property("border", "none")
                                    property("padding", "0.65rem 1.25rem")
                                    property("border-radius", "50px")
                                    property("font-weight", "700")
                                    property("font-size", "0.85rem")
                                    property("cursor", "pointer")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.45rem")
                                    property("box-shadow", "0 2px 8px rgba(0, 184, 151, 0.25)")
                                }
                            }) {
                                FaIcon("fa-solid fa-print", size = "0.85rem", color = "white")
                                Text("Öppna / Spara PDF")
                            }
                        }
                    }
                }
            }
        }
    }
}
